import { useState, useCallback } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useMutation } from '@apollo/client';
import {
  PayInvoicesQuery,
  PayInvoicesMutationResult,
  PayInvoicesMutationVariables,
} from '../../../schema/generated/schema';

export interface BillingAddress {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  countryCode: string;
  phoneNumber?: string;
}

export interface PaymentParams {
  invoiceRecordNos: number[];
  totalAmountInCents: number;
  billingAccountId: string;
  paymentType: 'new_card' | 'saved_card';
  stripeCardId?: string;
  saveCreditCard?: boolean;
  billingAddress?: BillingAddress;
}

interface UsePayInvoiceReturn {
  submitPayment: (params: PaymentParams) => Promise<boolean>;
  isProcessing: boolean;
  error: string | null;
  clearError: () => void;
}

export const usePayInvoice = (): UsePayInvoiceReturn => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [payInvoicesMutation] = useMutation<
    PayInvoicesMutationResult,
    PayInvoicesMutationVariables
  >(PayInvoicesQuery);

  const clearError = useCallback(() => setError(null), []);

  const submitPayment = useCallback(
    async (params: PaymentParams): Promise<boolean> => {
      setError(null);
      setIsProcessing(true);

      try {
        let stripeTokenId: string | undefined;
        let stripeCardId: string | undefined;

        if (params.paymentType === 'new_card') {
          if (!stripe || !elements) {
            setError('Payment system is loading, please wait.');
            setIsProcessing(false);
            return false;
          }

          const cardElement = elements.getElement(CardElement);
          if (!cardElement) {
            setError('Card input is not available. Please try again.');
            setIsProcessing(false);
            return false;
          }

          const tokenPayload: Record<string, string> = {};
          if (params.billingAddress) {
            tokenPayload.name = params.billingAddress.name;
            tokenPayload.address_line1 = params.billingAddress.street1;
            if (params.billingAddress.street2) {
              tokenPayload.address_line2 = params.billingAddress.street2;
            }
            tokenPayload.address_city = params.billingAddress.city;
            tokenPayload.address_state = params.billingAddress.state;
            tokenPayload.address_zip = params.billingAddress.zipcode;
            tokenPayload.address_country = params.billingAddress.countryCode;
          }

          const result = await stripe.createToken(cardElement, tokenPayload);

          if (result.error) {
            setError(result.error.message ?? 'Card validation failed.');
            setIsProcessing(false);
            return false;
          }

          stripeTokenId = result.token?.id;
          if (!stripeTokenId) {
            setError('Failed to create payment token. Please try again.');
            setIsProcessing(false);
            return false;
          }
        } else {
          stripeCardId = params.stripeCardId;
          if (!stripeCardId) {
            setError('No saved payment method selected.');
            setIsProcessing(false);
            return false;
          }
        }

        const invoiceRecordNosAsInts = params.invoiceRecordNos.map((n) =>
          typeof n === 'number' ? n : parseInt(String(n), 10)
        );

        const { data } = await payInvoicesMutation({
          variables: {
            input: {
              invoiceRecordNos: invoiceRecordNosAsInts,
              billingAccountId: params.billingAccountId,
              totalAmountInCents: params.totalAmountInCents,
              payment: {
                stripeTokenId: stripeTokenId ?? null,
                stripeCardId: stripeCardId ?? null,
                saveCreditCard: params.saveCreditCard ?? null,
                billingAddress: params.billingAddress
                  ? {
                      name: params.billingAddress.name,
                      street1: params.billingAddress.street1,
                      street2: params.billingAddress.street2 ?? null,
                      city: params.billingAddress.city,
                      state: params.billingAddress.state,
                      zipcode: params.billingAddress.zipcode,
                      country: params.billingAddress.country,
                      countryCode: params.billingAddress.countryCode,
                      phoneNumber: params.billingAddress.phoneNumber ?? null,
                    }
                  : null,
              },
            },
          },
        });

        const result = data?.payInvoices;

        if (!result?.successful) {
          setError(result?.errorMessage ?? 'Payment failed. Please try again.');
          setIsProcessing(false);
          return false;
        }

        setIsProcessing(false);
        return true;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        setError(`Payment failed: ${message}`);
        setIsProcessing(false);
        return false;
      }
    },
    [stripe, elements, payInvoicesMutation]
  );

  return { submitPayment, isProcessing, error, clearError };
};
